import { getBootstrapData } from "@swa-ui/bootstrap";
import { dateFormats, swaDate } from "@swa-ui/date";
import { regexes } from "@swa-ui/form";
import { stationMethods } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";
import { yup } from "@swa-ui/object";

import { MULTICITY, ONEWAY, ROUNDTRIP } from "../defines/tripType";

const getDateSchema = (lastBookableDate) =>
  yup
    .string()
    .test(
      "LastBookableDate",
      i18n("schema__ERROR_MAX_DATE", lastBookableDate.format("MM/DD/YYYY")),
      (value) => {
        let valid = true;

        if (value) {
          valid = swaDate(value).isBefore(lastBookableDate, "day");
        }

        return valid;
      }
    );

export const flightBookingFormSchema = () => {
  const airDatesData = getBootstrapData("air/air-dates-data");
  const stationIds = getBootstrapData("air/stations-data").map((station) => station.id);
  const lastBookableDate = swaDate(airDatesData.currentLastBookableDate).add(1, "day");
  const firstBookableDate = swaDate().set("date", swaDate().get("date"));

  return yup.object().shape(
    {
      tripType: yup.string().trim().oneOf([ONEWAY, ROUNDTRIP, MULTICITY]),
      originationAirportCode: yup.mixed().when("tripType", {
        is: MULTICITY,
        then: yup
          .string()
          .trim()
          .required()
          .test(
            "ValidDepartureAirport",
            i18n("schema__VALID_DEPARTURE_AIRPORT"),
            (originationAirportCode) =>
              stationMethods.getStation(originationAirportCode) !== undefined
          )
          .label(i18n("schema__DEPARTURE_AIRPORT").toLowerCase()),
        otherwise: yup.lazy((val) =>
          Array.isArray(val)
            ? yup
                .array()
                .min(1, i18n("schema__DEPARTURE_AIRPORT_REQUIRED"))
                .required()
                .label(i18n("schema__DEPARTURE_AIRPORT").toLowerCase())
            : yup
                .string()
                .trim()
                .required()
                .test(
                  "ValidDepartureAirport",
                  i18n("schema__VALID_DEPARTURE_AIRPORT"),
                  (originationAirportCode) =>
                    stationMethods.getStation(originationAirportCode) !== undefined
                )
                .label(i18n("schema__DEPARTURE_AIRPORT").toLowerCase())
        ),
      }),
      destinationAirportCode: yup.mixed().when("tripType", {
        is: MULTICITY,
        then: yup
          .string()
          .required()
          .test(
            "ValidArrivalAirport",
            i18n("schema__VALID_DESTINATION1_AIRPORT"),
            (destinationAirportCode) =>
              stationMethods.getStation(destinationAirportCode) !== undefined
          )
          .label(i18n("schema__DESTINATION1_AIRPORT").toLowerCase()),
        otherwise: yup.lazy((val) =>
          Array.isArray(val)
            ? yup
                .array()
                .min(1, i18n("schema__ARRIVAL_AIRPORT_REQUIRED"))
                .required()
                .label(i18n("schema__ARRIVAL_AIRPORT").toLowerCase())
            : yup
                .string()
                .required()
                .test(
                  "ValidArrivalAirport",
                  i18n("schema__VALID_ARRIVAL_AIRPORT"),
                  (destinationAirportCode) =>
                    stationMethods.getStation(destinationAirportCode) !== undefined
                )
                .label(i18n("schema__ARRIVAL_AIRPORT").toLowerCase())
        ),
      }),
      returnAirportCode: yup.string().when("returnAirportCode", {
        is: (value) => !!value,
        then: yup
          .string()
          .oneOf(stationIds)
          .test(
            "ValidReturnAirport",
            i18n("schema__VALID_DESTINATION2_AIRPORT"),
            (returnAirportCode) => stationMethods.getStation(returnAirportCode) !== undefined
          )
          .label(i18n("schema__DESTINATION2_AIRPORT").toLowerCase()),
        otherwise: yup
          .string()
          .transform((value, originalReturnAirportCode) => {
            // Convert empty values to null
            if (!value) {
              return null;
            }

            return originalReturnAirportCode;
          })
          .nullable()
          .optional(),
      }),
      departureDate: yup
        .string()
        .required()
        .test("ValidDate", i18n("schema__VALID_DEPART_DATE"), (departureDate) =>
          swaDate(departureDate, dateFormats.default, true).isValid()
        )
        .test(
          "FirstBookableDate",
          i18n("schema__ERROR_MIN_DATE", firstBookableDate.format("MM/DD/YYYY")),
          (departureDateValue) => swaDate(departureDateValue).isSameOrAfter(swaDate(), "day")
        )
        .test(
          "LastBookableDate",
          i18n("schema__ERROR_MAX_DATE", lastBookableDate.format("MM/DD/YYYY")),
          (departureDateValue) => swaDate(departureDateValue).isBefore(lastBookableDate, "day")
        )
        .label(i18n("schema__DEPART_DATE").toLowerCase()),
      returnDate: yup
        .string()
        .required()
        .when("tripType", (tripType) => {
          let validation = "";

          if (tripType === MULTICITY) {
            validation = getDateSchema(lastBookableDate)
              .required()
              .label(i18n("schema__DEPART_DATE").toLowerCase())
              .when("departDate", (departDate, returnDateTestSchema) =>
                returnDateTestSchema.test(
                  "BeforeDepartureDate",
                  i18n("schema__ERROR_MIN_DATE", firstBookableDate.format("MM/DD/YYYY")),
                  (returnDateValue) =>
                    swaDate(returnDateValue, dateFormats.default, true).isValid() &&
                    swaDate(returnDateValue).isSameOrAfter(departDate, "day")
                )
              );
          } else if (tripType === ROUNDTRIP) {
            validation = getDateSchema(lastBookableDate)
              .required()
              .label(i18n("schema__RETURN_DATE").toLowerCase())
              .when("departDate", (departDate, returnDateTestSchema) =>
                returnDateTestSchema.test(
                  "BeforeDepartureDate",
                  i18n("schema__ERROR_MIN_DATE", firstBookableDate.format("MM/DD/YYYY")),
                  (returnDateValue) =>
                    swaDate(returnDateValue, dateFormats.default, true).isValid() &&
                    swaDate(returnDateValue).isSameOrAfter(departDate, "day")
                )
              );
          } else {
            validation = yup.string().notRequired();
          }

          return validation;
        }),
      adultPassengersCount: yup
        .string()
        .test(
          "adultPassengersCount",
          i18n("schema__ERROR_MIN_PASSENGER_COUNT"),
          (adultPassengersCount) => parseInt(adultPassengersCount, 10) > 0
        )
        .label(i18n("schema__PASSENGER_COUNT")),
      promoCode: yup
        .string()
        .trim()
        .max(10)
        .matches(regexes.alphaNumeric)
        .label(i18n("schema__PROMOCODE").toLowerCase()),
      showFareInPoints: yup.bool().oneOf([true, false]).default(false),
      lowFareCalendar: yup.bool().oneOf([true, false]).default(false),
    },
    [["returnAirportCode", "returnAirportCode"]]
  );
};
